import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { publicApi } from '@/lib/api/publicClient';
import { xurveyPublicUrl } from '@/lib/api/xurvey';
import { useT } from '@/lib/i18n/useT';

/**
 * La dirección pública antigua de una estación, redirigida a Xurvey.
 *
 * Aquí vivía el wizard de Xotski: su propio renderer de encuestas, con sus
 * ocho juegos, hablando con `/xy-xurvey/:id/:stationId/object` y
 * `/xy-xurvey-response/bulk`. Ya no. El runtime de las encuestas es de Xurvey y
 * Xotski las enseña **por URL** (protocolo §2.4). Tener dos renderers del mismo
 * formato garantizaba que divergieran, y arreglar algo en uno dejaba el otro
 * roto sin que nadie se enterara hasta el siguiente evento.
 *
 * **La ruta sigue existiendo y eso no es provisional.** Hay QR impresos, pegados
 * a neveras y en carteles, que apuntan aquí; seguirán llegando visitas durante
 * años. Redirigir es lo único que respeta ese cartón.
 *
 * La redirección se resuelve contra el despliegue —`/public/xurveys/resolve/…`,
 * anónima a propósito— porque quien escanea no tiene sesión. Si esa estación no
 * está desplegada, se dice sin tecnicismos: el invitado no puede hacer nada al
 * respecto, pero quien gestiona el evento sí.
 */
export function PublicSurveyPage() {
  const [params] = useSearchParams();
  const surveyId = params.get('surveyId');
  const stationId = params.get('stationId');
  const preview = params.get('preview') === '1';
  const t = useT();

  const enlace = useQuery({
    queryKey: ['xurvey', 'resolve', surveyId, stationId],
    queryFn: () =>
      publicApi.get<{ slug: string }>(
        `/public/xurveys/resolve/xotski/station/${stationId}/${surveyId}`,
      ),
    enabled: !!surveyId && !!stationId,
    // Un QR mal impreso o una estación retirada no mejoran reintentando, y
    // cada reintento es medio segundo más de pantalla en blanco delante de
    // alguien que está de pie en un stand.
    retry: false,
  });

  const slug = enlace.data?.slug;

  useEffect(() => {
    if (!slug) return;
    // `replace` y no `href`: quien vuelva atrás desde la encuesta debe salir a
    // donde estaba, no rebotar contra esta redirección una y otra vez.
    window.location.replace(xurveyPublicUrl(slug, preview ? { preview: true } : undefined));
  }, [slug, preview]);

  if (!surveyId || !stationId) return <Aviso>{t('publicSurvey.missingSurveyOrStation')}</Aviso>;
  if (enlace.isLoading || slug) return <Aviso>{t('common.loading')}</Aviso>;
  return <Aviso>{t('publicSurvey.notDeployed')}</Aviso>;
}

const Aviso = ({ children }: { children: React.ReactNode }) => (
  <div className="grid min-h-screen place-items-center px-6 text-center text-white">
    <p className="max-w-sm text-sm leading-relaxed">{children}</p>
  </div>
);
